/**
 * SEGMENTET: KAPSEL BETYR «HVORDAN SER JEG DET» (Glass 2, DESIGN.md §16.5 punkt 3).
 *
 * Uke/Dag, Liste/Uke/Dag/Kart, Påmeldte/Skjema: ett valg av flere, alle synlige, om det SAMME
 * innholdet. Seksjoner av et objekt («hvor er jeg») er faner med understrek — `Tabs.tsx` — og de to
 * formene blandes aldri.
 *
 * Tegningen: et tonalt spor i kapselform med to piksler luft, og det valgte segmentet som en hvit
 * (mørk: lys glass-) kapsel med sin egen lille skygge (`--segment-active-bg`,
 * `--shadow-segment-active`). Det valgte er merket med `aria-pressed` i tillegg, så valget aldri
 * bæres av flaten alene (§3).
 *
 * KAPSEL I KAPSEL (sak 229.13). Pillen var 9 px i et spor på 999, og hjørnene sto ikke konsentrisk —
 * Atle så det på Bookinger. Med samme radius og to piksler luft følger pillen sporets kurve, slik
 * `PeriodNav` alltid har gjort.
 *
 * PILLEN GLIR. Den er ett eget lag under knappene som måles mot det valgte segmentet og flyttes med
 * `transform`, så et bytte ser ut som én flate som flytter seg og ikke to som blinker. Før den er
 * målt (første maling, jsdom, en skjult fane) tegner det valgte segmentet flaten selv, så det valgte
 * aldri er usynlig. Redusert bevegelse gir et hopp uten gli.
 *
 * INGENTING VALGT ER EN VERDI (QA16 funn 5). `value={null}` tegner sporet uten pille: ingen av valgene
 * ser valgt ut før brukeren har valgt. Før måtte kallstedet gi en verdi, og «Send bud nå» ble tegnet som
 * valgt mens flyten sa «Velg hvordan avtalen er gjort». Et valgt segment skiller seg fra de andre på
 * flaten, vekten og fargen, og et ikke-valgt er dempet.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { ICONS, type IconName } from './icons';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
  /** Et tall etter ordet — «Påmeldte 3». Dempet, i tabulære siffer. */
  count?: number;
  /** Segmentets egen `data-testid`, når den ikke er `<gruppas testId>-<value>`. */
  testId?: string;
}

export interface SegmentedProps<T extends string> {
  /** The group's accessible name. */
  label: string;
  /** `null`: ingenting er valgt ennå. */
  value: T | null;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  /** `data-testid` på gruppa; hvert segment får `<testId>-<value>`. */
  testId?: string;
  /** Hele valget låst — f.eks. mens det lagres, eller for en rolle som bare kan se. */
  disabled?: boolean;
}

interface ThumbBox {
  left: number;
  width: number;
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
  testId,
  disabled = false,
}: SegmentedProps<T>): ReactNode {
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<ThumbBox | null>(null);
  /* Første plassering skal ikke gli inn fra venstre kant; bare bytter etterpå glir. */
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const measure = (): void => {
      const selected = track.querySelector<HTMLElement>('[aria-pressed="true"]');
      if (!selected || selected.offsetWidth === 0) {
        setThumb(null);
        return;
      }
      setThumb((prev) =>
        prev?.left === selected.offsetLeft && prev.width === selected.offsetWidth
          ? prev
          : { left: selected.offsetLeft, width: selected.offsetWidth },
      );
    };
    measure();
    // Tallene i segmentene («Alle 2») og språket endrer bredden uten at valget endres.
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [value, options]);

  useLayoutEffect(() => {
    if (thumb !== null && !animate) {
      const frame = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  }, [thumb, animate]);

  const thumbDrawn = thumb !== null;

  return (
    <div
      ref={trackRef}
      role="group"
      aria-label={label}
      data-testid={testId}
      className={cn(
        'segmented-track relative inline-flex shrink-0 items-center gap-0.5 p-0.5',
        // Sporet er så bredt som valgene, også i en kolonne (QA16 funn 5): strukket over hele bredden
        // så det ikke-valgte ut som en tekst på en grå stripe, ikke som et valg.
        'w-fit max-w-full',
        // Det tonale fyllet OVER kontrollfyllet: på bakgrunnen er kontrollfyllet hvitt glass (i
        // «Flat + Av» helt hvitt), og det valgte segmentet er også hvitt. Uten den grå tonen
        // oppå ville bare skyggen skilt dem. Inne i et kort er kontrollfyllet ikke satt, og
        // sporet er bare tonalt.
        'rounded-[var(--radius-control)]',
        '[background:linear-gradient(var(--fill-tonal),var(--fill-tonal)),var(--control-fill,transparent)]',
        className,
      )}
    >
      {thumbDrawn ? (
        <span
          aria-hidden
          data-segmented-thumb
          className={cn(
            'pointer-events-none absolute inset-y-0.5 left-0 rounded-[var(--radius-control)]',
            'bg-[var(--segment-active-bg)] shadow-[var(--shadow-segment-active)]',
            animate &&
              'transition-[transform,width] duration-[var(--duration-base)] ease-[var(--ease-out)] motion-reduce:transition-none',
          )}
          style={{ width: thumb.width, transform: `translateX(${String(thumb.left)}px)` }}
        />
      ) : null}
      {options.map((option) => {
        const Icon = option.icon ? ICONS[option.icon] : null;
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            {...(option.testId !== undefined
              ? { 'data-testid': option.testId }
              : testId === undefined
                ? {}
                : { 'data-testid': `${testId}-${option.value}` })}
            /* Segmentet er en del av sporet; sporet er rammen (se buttons-look-like-buttons). */
            data-unstyled
            className={cn(
              // `hit-target` gir 44 px å treffe uten å gjøre segmentet større (§9). Segmentene er
              // bredere enn 44, så to naboers flater overlapper ikke.
              'hit-target relative inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-control)] px-3',
              'text-xs whitespace-nowrap ring-focus',
              'transition-[background-color,color,box-shadow] duration-[var(--duration-fast)]',
              selected
                ? cn(
                    'font-semibold text-default',
                    !thumbDrawn &&
                      'bg-[var(--segment-active-bg)] shadow-[var(--shadow-segment-active)]',
                  )
                : 'font-medium text-muted hover:text-default',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {Icon ? <Icon aria-hidden className="size-3.5 shrink-0" /> : null}
            {option.label}
            {option.count === undefined ? null : (
              <span className="numeric text-muted">{option.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
